// Same-origin API/auth helpers. Auth is a backend-for-frontend flow: the cookie
// is httpOnly and sent automatically, so there are no tokens to manage here. The
// one rule the client must honour is 401 → bounce to login.

import type { ProgressLine } from "./types.js";

/** Redirect to the BFF login route, preserving where the user was. */
export function redirectToLogin(): void {
  const returnTo = location.pathname + location.search;
  location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * fetch() wrapper for /api calls: on a 401 (expired/absent session) it redirects
 * to login and returns a promise that never resolves, so callers don't run on
 * past the navigation.
 */
export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const resp = await fetch(input, init);
  if (resp.status === 401) {
    redirectToLogin();
    return new Promise<Response>(() => {});
  }
  return resp;
}

/**
 * Read an NDJSON stream from `input`: forward each `progress` line to
 * `onProgress`, resolve with the terminal `result` payload, throw on an `error`
 * line. Preserves apiFetch's 401 → login behaviour (checked before reading the
 * body), so streaming callers get the same auth handling as plain fetches.
 */
export async function apiFetchStream<T>(
  input: string,
  onProgress: (p: ProgressLine) => void,
): Promise<T> {
  const resp = await fetch(input);
  if (resp.status === 401) {
    redirectToLogin();
    return new Promise<T>(() => {});
  }
  if (!resp.ok || !resp.body) {
    const body = (await resp.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `HTTP ${resp.status}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let result: T | undefined;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      const msg = JSON.parse(line) as { type: string; error?: string };
      if (msg.type === "progress") onProgress(msg as unknown as ProgressLine);
      else if (msg.type === "error") throw new Error(msg.error || "Stream error");
      else if (msg.type === "result") result = msg as unknown as T;
    }
  }
  if (result === undefined) throw new Error("Stream ended without a result");
  return result;
}

export interface Me {
  email: string;
  isAdmin: boolean;
  all_sites_access: boolean;
}

/**
 * Identify the current user. Returns null when there's no session (401), so the
 * caller can show the login landing rather than bouncing straight to the IdP.
 */
export async function fetchMe(): Promise<Me | null> {
  const resp = await fetch("/auth/me");
  if (resp.status === 401) return null;
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return (await resp.json()) as Me;
}

/** End the session and return to the start (which will re-trigger login). */
export async function logout(): Promise<void> {
  await fetch("/auth/logout", { method: "POST" });
  location.href = "/";
}
